import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { registerThunk } from '../../features/auth/authThunks';
import './Register.scss';
const Register = () => {
    const navigate = useNavigate();
    const { register } = useAuth();
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        password: '',
        confirmPassword: '',
        agreeToTerms: false,
    });
    const [errors, setErrors] = useState({});
    const [isLoading, setIsLoading] = useState(false);
    const [showAlert, setShowAlert] = useState(false);
    const [alertMessage, setAlertMessage] = useState('');
    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value,
        }));
        // Clear error when user starts typing
        if (errors[name]) {
            setErrors(prev => ({
                ...prev,
                [name]: '',
            }));
        }
    };
    const validateForm = () => {
        const newErrors = {};
        if (!formData.name.trim()) {
            newErrors.name = 'Name is required';
        }
        else if (formData.name.trim().length < 2) {
            newErrors.name = 'Name must be at least 2 characters';
        }
        if (!formData.email) {
            newErrors.email = 'Email is required';
        }
        else if (!/\S+@\S+\.\S+/.test(formData.email)) {
            newErrors.email = 'Email is invalid';
        }
        if (!formData.password) {
            newErrors.password = 'Password is required';
        }
        else if (formData.password.length < 6) {
            newErrors.password = 'Password must be at least 6 characters';
        }
        if (!formData.confirmPassword) {
            newErrors.confirmPassword = 'Please confirm your password';
        }
        else if (formData.password !== formData.confirmPassword) {
            newErrors.confirmPassword = 'Passwords do not match';
        }
        if (!formData.agreeToTerms) {
            newErrors.agreeToTerms = 'You must agree to the terms and conditions';
        }
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };
    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validateForm()) {
            return;
        }
        setIsLoading(true);
        setShowAlert(false);
        try {
            const result = await register({
                name: formData.name.trim(),
                email: formData.email,
                password: formData.password,
                confirmPassword: formData.confirmPassword,
            });
            // Check if registration was successful using Redux Toolkit's action matching
            if (registerThunk.fulfilled.match(result)) {
                // Registration successful, redirect to dashboard
                navigate('/');
            }
            else {
                // Registration failed
                const errorMessage = result.payload || 'Registration failed. Email might already be in use.';
                setAlertMessage(errorMessage);
                setShowAlert(true);
            }
        }
        catch (error) {
            setAlertMessage('An error occurred during registration. Please try again.');
            setShowAlert(true);
            console.error('Registration failed:', error);
        }
        finally {
            setIsLoading(false);
        }
    };
    return (_jsx("div", { className: "register-page", children: _jsx("div", { className: "container", children: _jsx("div", { className: "row justify-content-center align-items-center min-vh-100", children: _jsx("div", { className: "col-md-6 col-lg-5", children: _jsx("div", { className: "card register-card", children: _jsxs("div", { className: "card-body p-4", children: [_jsxs("div", { className: "text-center mb-4", children: [_jsx("h2", { className: "register-title", children: "Create Account" }), _jsx("p", { className: "text-muted", children: "Join our design system community" })] }), showAlert && (_jsxs("div", { className: "alert alert-danger alert-dismissible", role: "alert", children: [_jsx("div", { children: alertMessage }), _jsx("button", { type: "button", className: "btn-close", onClick: () => setShowAlert(false) })] })), _jsxs("form", { onSubmit: handleSubmit, children: [_jsxs("div", { className: "mb-3", children: [_jsx("label", { className: "form-label", children: "Full Name" }), _jsx("input", { type: "text", name: "name", value: formData.name, onChange: handleInputChange, placeholder: "Enter your full name", className: `form-control ${errors.name ? 'is-invalid' : ''}` }), errors.name && (_jsx("div", { className: "invalid-feedback", children: errors.name }))] }), _jsxs("div", { className: "mb-3", children: [_jsx("label", { className: "form-label", children: "Email address" }), _jsx("input", { type: "email", name: "email", value: formData.email, onChange: handleInputChange, placeholder: "Enter your email", className: `form-control ${errors.email ? 'is-invalid' : ''}` }), errors.email && (_jsx("div", { className: "invalid-feedback", children: errors.email }))] }), _jsxs("div", { className: "mb-3", children: [_jsx("label", { className: "form-label", children: "Password" }), _jsx("input", { type: "password", name: "password", value: formData.password, onChange: handleInputChange, placeholder: "Create a password", className: `form-control ${errors.password ? 'is-invalid' : ''}` }), errors.password && (_jsx("div", { className: "invalid-feedback", children: errors.password }))] }), _jsxs("div", { className: "mb-3", children: [_jsx("label", { className: "form-label", children: "Confirm Password" }), _jsx("input", { type: "password", name: "confirmPassword", value: formData.confirmPassword, onChange: handleInputChange, placeholder: "Confirm your password", className: `form-control ${errors.confirmPassword ? 'is-invalid' : ''}` }), errors.confirmPassword && (_jsx("div", { className: "invalid-feedback", children: errors.confirmPassword }))] }), _jsx("div", { className: "mb-3", children: _jsxs("div", { className: "form-check", children: [_jsx("input", { type: "checkbox", name: "agreeToTerms", checked: formData.agreeToTerms, onChange: handleInputChange, className: `form-check-input ${errors.agreeToTerms ? 'is-invalid' : ''}` }), _jsx("label", { className: "form-check-label", children: "I agree to the terms and conditions" }), errors.agreeToTerms && (_jsx("div", { className: "text-danger small mt-1", children: errors.agreeToTerms }))] }) }), _jsx("div", { className: "d-grid mb-3", children: _jsx("button", { type: "submit", disabled: isLoading, className: "btn btn-primary register-button", children: isLoading ? 'Creating Account...' : 'Create Account' }) }), _jsx("div", { className: "text-center", children: _jsxs("p", { className: "mb-0 text-muted", children: ["Already have an account?", ' ', _jsx("a", { href: "/login", className: "text-decoration-none", children: "Sign in" })] }) })] })] }) }) }) }) }) }));
};
export default Register;
